import React, { useEffect, useState } from 'react';
import { JobTitleApi } from 'lib/api/job-title';
import {
  Button,
  CircularProgress,
  Pagination,
  Stack,
  TextField,
} from '@mui/material';
import AddBoxIcon from '@mui/icons-material/AddBox';
import PostAddIcon from '@mui/icons-material/PostAdd';
import CustomizedTable from 'modules/job-titles/components/Table';
import EditButton from 'lib/components/EditButton';
import DeleteButton from 'lib/components/DeleteButton';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Container from '@mui/material/Container';
import CustomizedSnackbars from 'lib/components/SnackBar';
import Tooltip from '@mui/material/Tooltip';
import {
  BaseCardBox,
  Header,
  BaseTextField,
  BaseDialog,
  BaseGetAppIcon,
} from './style';
import axiosInstance from 'lib/api/api';
import { FileApi } from '../../../../lib/api/file';
import { useSnackbar } from 'notistack';
import { useUser } from '../../../auth/context/auth.consumer';
import { RoleType } from '../../../auth/common/role';
import { BaseExportExcelContainer } from '../../../sectors/pages/list/style';
import IosShareIcon from '@mui/icons-material/IosShare';

interface IJobTitle {
  id?: string;
  name: string;
}

interface ITableBody {
  name: string;
  actions: JSX.Element;
}

interface ISnackBarSettings {
  show: boolean;
  message?: string;
  severity?: 'success' | 'error' | 'warning' | 'info';
}

const JobTitlesPage = () => {
  const [jobTitles, setJobTitles] = useState<IJobTitle[]>();
  const [search, setSearch] = useState<string>('');
  const [tableBody, setTableBody] = useState<ITableBody[]>([]);
  const [open, setOpen] = useState(false);
  const [currentId, setcurrentId] = useState<string>('');
  const [jobTitle, setJobTitle] = useState<IJobTitle>();
  const [isEdit, setIsEdit] = useState(false);
  const [snackBarSettings, setSnackBarSettings] = useState<ISnackBarSettings>({
    show: false,
  });
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState<number | undefined>(undefined);

  const [excelLoading, setExcelLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const user = useUser();

  const tableHead = ['Cargo', 'Ações'];

  const getJobTitlesPaginated = async () => {
    const response = await JobTitleApi.searchJobTitles(search, page);
    setJobTitles(response.entities);
    setPageCount(response.meta.pageCount);
  };

  useEffect(() => {
    getJobTitlesPaginated();
  }, [page, search]);

  const resetSnackBar = () => {
    setTimeout(() => {
      setSnackBarSettings({ show: false });
    }, 3000);
  };

  const handleSaveJobTitleModal = (e: React.FormEvent) => {
    e.preventDefault();
    handleCloseModalForm();
    if (isEdit) {
      handleEdit();
      getJobTitlesPaginated();
      return;
    } else {
      handleCreate();
      getJobTitlesPaginated();
    }
  };

  const handleCreate = async () => {
    try {
      if (jobTitle) {
        await JobTitleApi.createJobTitle(jobTitle);
        setSnackBarSettings({
          show: true,
          message: 'Cargo criado com sucesso!',
          severity: 'success',
        });
        getJobTitlesPaginated();
        resetSnackBar();
      }
    } catch (err: any) {
      setSnackBarSettings({
        show: true,
        message: err.message,
        severity: 'error',
      });
      resetSnackBar();
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await JobTitleApi.deleteJobTitle(id);
      setSnackBarSettings({
        show: true,
        message: 'Cargo excluido com sucesso!',
        severity: 'success',
      });
      getJobTitlesPaginated();
      resetSnackBar();
    } catch (err: any) {
      setSnackBarSettings({
        show: true,
        message: err.response.data.message,
        severity: 'error',
      });
      resetSnackBar();
    }
  };

  const handleEdit = async () => {
    try {
      if (jobTitle) {
        await JobTitleApi.editJobTitle(currentId, jobTitle);
        setSnackBarSettings({
          show: true,
          message: 'Cargo editado com sucesso!',
          severity: 'success',
        });
        getJobTitlesPaginated();
        resetSnackBar();
      }
    } catch (err: any) {
      setSnackBarSettings({
        show: true,
        message: err.message,
        severity: 'error',
      });
      resetSnackBar();
    }
  };

  function editCallback(isOpen: boolean, jobTitleName: string) {
    setJobTitle({ name: jobTitleName });
    setOpen(isOpen);
    setIsEdit(true);
  }

  const handleFile = async (e: any) => {
    if (e.target.files || e.target.files.length !== 0) {
      const formData = new FormData();

      formData.append('file', e.target.files[0]);

      try {
        axiosInstance.post(
          `/file-upload/upload/entity-spreadsheet?sheetEntity=JOBTITLE`,
          formData,
        );
        setTimeout(() => {
          getJobTitlesPaginated();
        }, 500);
        setSnackBarSettings({
          show: true,
          message: 'Arquivo importado com sucesso!',
          severity: 'success',
        });
        resetSnackBar();
      } catch (err: any) {
        setSnackBarSettings({
          show: true,
          message: err.message,
          severity: 'error',
        });
        resetSnackBar();
      }
    }
  };

  const mountTableBody = () => {
    jobTitles?.map((item) => {
      setTableBody((prev) => [
        ...prev,
        {
          name: item.name,
          actions: (
            <div>
              <EditButton
                item={item}
                currentItem={setcurrentId}
                callback={editCallback}
              />
              <DeleteButton id={item.id} callback={handleDelete} />
            </div>
          ),
        },
      ]);
    });
  };

  useEffect(() => {
    setTableBody([]);
    mountTableBody();
  }, [jobTitles]);

  const handleCloseModalForm = () => {
    setOpen(false);
  };

  const getExportExcelJobTitles = async () => {
    setExcelLoading(true);
    try {
      const url = await FileApi.exportJobTitles();
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', url);
      document.body.appendChild(link);
      link.click();
    } catch (e) {
      setExcelLoading(false);
      console.error(e);
      enqueueSnackbar('Erro ao exportar os cargos', {
        variant: 'error',
        autoHideDuration: 6000,
      });
    } finally {
      setExcelLoading(false);
    }
  };

  return (
    <Container>
      <BaseCardBox>
        <Header>
          <h3>Cadastro de Cargos</h3>
          <div>
            <Tooltip title="Baixar planilha de importação">
              <a href="https://msbackend.blob.core.windows.net/planilha-entidade/cargos.xlsx">
                <BaseGetAppIcon />
              </a>
            </Tooltip>

            <input id="upload" type="file" onChange={(e) => handleFile(e)} />
            <label htmlFor="upload">
              <PostAddIcon />
              Importar
            </label>

            <Button
              onClick={() => {
                setJobTitle({ name: '' });
                setIsEdit(false);
                setOpen(true);
              }}
              variant="contained"
              startIcon={<AddBoxIcon />}
            >
              Novo Cadastro
            </Button>
          </div>
        </Header>

        <BaseTextField
          onChange={(event) => {
            page !== 1 && setPage(1);
            setSearch(event.target.value);
          }}
          label="Realizar busca"
          variant="outlined"
        />
      </BaseCardBox>

      {user && user.role === RoleType.ADMIN && (
        <BaseExportExcelContainer>
          <div>
            <Tooltip title="Exportar em excel todos os registros de cargos">
              <Button
                variant="contained"
                startIcon={
                  excelLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    <IosShareIcon />
                  )
                }
                onClick={() => getExportExcelJobTitles()}
                disabled={excelLoading}
              >
                Exportar em excel
              </Button>
            </Tooltip>
          </div>
        </BaseExportExcelContainer>
      )}

      <CustomizedTable tableHead={tableHead} tableBody={tableBody} />

      <BaseDialog
        open={open}
        onClose={handleCloseModalForm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <form onSubmit={(e) => handleSaveJobTitleModal(e)}>
          {!isEdit ? (
            <DialogTitle id="alert-dialog-title">{'NOVO CARGO'}</DialogTitle>
          ) : (
            <DialogTitle id="alert-dialog-title">{'EDITAR CARGO'}</DialogTitle>
          )}

          <TextField
            required
            onChange={(event) =>
              setJobTitle((prev) => ({ ...prev, name: event.target.value }))
            }
            label="Nome do cargo"
            variant="outlined"
            value={jobTitle?.name}
          />
          <DialogActions>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCloseModalForm}
            >
              Cancelar
            </Button>
            <Button variant="contained" color="success" type="submit" autoFocus>
              Salvar
            </Button>
          </DialogActions>
        </form>
      </BaseDialog>

      {snackBarSettings.show && (
        <CustomizedSnackbars
          message={snackBarSettings.message}
          severity={snackBarSettings.severity}
        />
      )}
      <Stack spacing={2} alignItems={'center'}>
        <Pagination
          count={pageCount}
          page={page}
          onChange={(e, value) => setPage(value)}
          defaultPage={page}
        />
      </Stack>
    </Container>
  );
};

export { JobTitlesPage };
